import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { supabase } from '../../../lib/supabase';
import ForgotPasswordModal from '../../forgot-password-modal';

const LoginForm = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
  
    if (!validateForm()) return;
  
    setIsLoading(true);
  
    try {
      const { data, error } = await signIn(formData?.email, formData?.password);
  
      if (error) {
        setErrors({ submit: error?.message });
        return;
      }
  
      const user = data?.user;
      const userRole = user?.raw_user_meta_data?.role;
  
      // If student, fetch their team info from the roles (or profiles) table
      if (userRole === 'student') {
        const { data: roleData, error: roleError } = await supabase?.from('roles')?.select('team_number')?.eq('user_id', user?.id)?.single();
  
        if (roleError) {
          console.error('Error fetching team info:', roleError?.message);
          navigate('/team-workspace'); // fallback
          return;
        }
  
        const teamNumber = roleData?.team_number;
        navigate(`/team/${teamNumber}`); // ✅ takes them to their specific team space
        return;
      }
  
      // Other roles → normal dashboards
      const dashboardRoutes = {
        pcf_admin: '/pcf-admin-dashboard',
        city_official: '/city-official-dashboard',
      };
  
      navigate(dashboardRoutes?.[userRole] || '/team-workspace');
    } catch (error) {
      console.error('Login error:', error);
      setErrors({ submit: 'Login failed. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };
  

  const handleDemoLogin = async (email, password) => {
    setFormData({ email, password, rememberMe: false });
    setIsLoading(true);

    try {
      const { data, error } = await signIn(email, password);
      
      if (error) {
        setErrors({ submit: error?.message });
        return;
      }

      const userRole = data?.user?.raw_user_meta_data?.role;
      const dashboardRoutes = {
        pcf_admin: '/pcf-admin-dashboard',
        city_official: '/city-official-dashboard',
        student: '/team-workspace',
      };

      navigate(dashboardRoutes?.[userRole] || '/team-workspace');

    } catch (error) {
      setErrors({ submit: 'Login failed. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    setShowForgotPasswordModal(true);
  };

  const handleCreateAccount = () => {
    navigate('/user-registration');
  };

  return (
    <>
      <div className="w-full max-w-md mx-auto">
        <div className="bg-card border border-border rounded-lg shadow-soft p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center w-16 h-16 bg-primary rounded-full mx-auto mb-4">
              <Icon name="Building2" size={32} color="white" />
            </div>
            <h1 className="text-2xl font-semibold text-foreground mb-2">Welcome Back</h1>
            <p className="text-muted-foreground">Sign in to your Smart Cities account</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Input */}
            <Input
              label="Email Address"
              type="email"
              placeholder="Enter your email"
              value={formData?.email}
              onChange={(e) => handleInputChange('email', e?.target?.value)}
              error={errors?.email}
              required
              autoComplete="email"
            />

            {/* Password Input */}
            <div className="relative">
              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={formData?.password}
                onChange={(e) => handleInputChange('password', e?.target?.value)}
                error={errors?.password}
                required
                autoComplete="current-password"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-8 h-8 w-8"
              >
                <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
              </Button>
            </div>

            {/* Remember Me */}
            <div className="flex items-center justify-between">
              <Checkbox
                label="Remember me"
                checked={formData?.rememberMe}
                onChange={(e) => handleInputChange('rememberMe', e?.target?.checked)}
              />
              <Button
                type="button"
                variant="link"
                onClick={handleForgotPassword}
                className="text-sm"
              >
                Forgot password?
              </Button>
            </div>

            {/* Submit Error */}
            {errors?.submit && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <Icon name="AlertCircle" size={16} className="text-error" />
                  <span className="text-sm text-error">{errors?.submit}</span>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              variant="default"
              size="lg"
              fullWidth
              loading={isLoading}
              disabled={isLoading}
              iconName="LogIn"
              iconPosition="left"
            >
              {isLoading ? 'Signing In...' : 'Sign In'}
            </Button>
          </form>

          {/* Footer Actions */}
          <div className="mt-8 pt-6 border-t border-border">
            <div className="text-center space-y-4">
              <p className="text-sm text-muted-foreground">
                Don't have an account?{' '}
                <Button
                  variant="link"
                  onClick={handleCreateAccount}
                  className="text-sm p-0 h-auto"
                >
                  Create new account
                </Button>
              </p>
              
              <div className="flex items-center justify-center space-x-4 text-xs text-muted-foreground">
                <Button variant="link" className="text-xs p-0 h-auto">
                  Need Help?
                </Button>
                <span>•</span>
                <Button variant="link" className="text-xs p-0 h-auto">
                  Privacy Policy
                </Button>
                <span>•</span>
                <Button variant="link" className="text-xs p-0 h-auto">
                  Terms of Service
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Demo Credentials Section - Add after the form */}
        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="space-y-2 text-xs text-blue-800">
            <div className="mt-3 p-2 bg-blue-100 rounded border">
              <p className="text-blue-700 font-medium">⚠️ Role-Based Access Control Active</p>
              <p className="text-blue-600">
                Only pre-authorized emails can create accounts. Each email is restricted to its assigned role dashboard.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        isOpen={showForgotPasswordModal}
        onClose={() => setShowForgotPasswordModal(false)}
      />
    </>
  );
};

export default LoginForm;